import type { UpdateAppointmentTypeInput } from 'app/generated/gql/graphql'
import { useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { EditAppointmentTypeModal } from 'app/components/scheduling/EditAppointmentTypeModal'
import { AppointmentTypeType } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useAppointmentTypeStore } from 'app/stores/StoreContext'
import { appointmentTypesSchedulerColumns } from 'app/table-columns/appointment-types'

function sortByActiveScheduler(items: AppointmentTypeType[]): AppointmentTypeType[] {
  const enabled = items.filter((item) => item.activeScheduler)
  const disabled = items.filter((item) => !item.activeScheduler)
  return [...enabled, ...disabled]
}

export type UseSchedulingAppointmentTypesReturn = {
  rows: AppointmentTypeType[]
  isSyncing: boolean
  columns: ReturnType<typeof appointmentTypesSchedulerColumns>
  onRefreshClick: () => void
}

export function useSchedulingAppointmentTypes(): UseSchedulingAppointmentTypesReturn {
  const { locationId } = useParams()
  const appointmentTypeStore = useAppointmentTypeStore()
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentTypeType[]>([])
  const [excludableAppointmentTypes, setExcludableAppointmentTypes] = useState<any[]>([])
  const [rows, setRows] = useState<AppointmentTypeType[]>([])
  const [isSyncing, setIsSyncing] = useState(false)

  useEffect(() => {
    if (!locationId) return
    fetch(`/api/scheduling/locations/${locationId}/appointment-types`, { credentials: `include` })
      .then((res) => res.json())
      .then((data) => {
        const types = (data.appointmentTypes ?? []) as AppointmentTypeType[]
        setAppointmentTypes(types)
        setExcludableAppointmentTypes(data.excludableAppointmentTypes ?? [])
        setRows(sortByActiveScheduler(types))
      })
      .catch(() => {})
  }, [locationId])

  const onToggleActive = async (id: string, active: boolean) => {
    const result = await appointmentTypeStore.update(id, { activeScheduler: active })
    if (!result.error) {
      notifySuccess(`Appointment type status saved`)
      setRows((prev) =>
        sortByActiveScheduler(prev.map((row) => (row.id === id ? { ...row, activeScheduler: active } : row))),
      )
    } else {
      notifyError(`Error saving appointment type status`)
    }
  }

  const onAppointmentTypeUpdated = (appointmentType: AppointmentTypeType) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id === appointmentType.id) {
          return appointmentType
        }
        return row
      }),
    )
  }

  const handleSaveAppointmentType = async (id: string, input: UpdateAppointmentTypeInput) => {
    const result = await appointmentTypeStore.update(id, input)
    if (result.error) return undefined
    const data = result.data as { updateAppointmentType?: AppointmentTypeType } | undefined
    return data?.updateAppointmentType
  }

  const openEditAppointmentTypeModal = async (appointmentType: AppointmentTypeType) => {
    showModal(
      <EditAppointmentTypeModal
        onAppointmentTypeUpdated={onAppointmentTypeUpdated}
        appointmentType={appointmentType}
        excludableAppointmentTypes={excludableAppointmentTypes}
        onSave={handleSaveAppointmentType}
      />,
      {
        title: `Edit Appointment type`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
    )
  }

  const onRefreshClick = async () => {
    setIsSyncing(true)
    fetch(`/api/scheduling/locations/${locationId}/appointment-types`, {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify({}),
    })
      .then((res) => res.json())
      .then((data) => {
        setIsSyncing(false)
        if (data?.success) {
          notifySuccess(
            data.added
              ? `${data.added} New Appointment Types Added`
              : `No New Appointment Types Added` +
                  (data.deleted ? `,\n ${data.deleted} Appointment Types Deleted` : ``),
          )
          if (data.appointmentTypes) {
            setRows(sortByActiveScheduler(data.appointmentTypes as AppointmentTypeType[]))
          }
        } else {
          notifyError(`Error syncing appointment types`)
        }
      })
  }

  const columns = useMemo(() => appointmentTypesSchedulerColumns(onToggleActive, openEditAppointmentTypeModal), [])

  return { rows, isSyncing, columns, onRefreshClick }
}
